import { HTTPError } from 'ky';
import { isLockConflictError } from '@/lib/locks/types';
import { isRebookingConflictError } from './api';
import type { FlightProviderAttempt, PassengerAge } from './types';

// «Cargar edades» — Duffel and Atlas quote a child (CHD) or an infant (INF) only with its exact
// age; adults are quoted as adults. When the airline holds no date of birth for one (apps/airline
// computes the age from it — the date itself never leaves it), the providers decline
// `passenger_ages_required` and the operator enters the ages here. Holds rules and keys, never
// language: the copy lives under `pnr:ages`.

/** The provider reasons the operator answers with «Cargar edades». */
const AGE_REASONS: ReadonlySet<string> = new Set(['passenger_ages_required', 'passenger_age_type_mismatch']);

/** Whole years a passenger type may be on the travel date (an infant is under 2). */
export const AGE_RANGE = { CHD: [2, 11], INF: [0, 1] } as const satisfies Record<string, readonly [number, number]>;

export type AgedPaxType = keyof typeof AGE_RANGE;

/** A provider asked for this PNR declined for want of an age (or an age that does not fit). */
export function needsPassengerAges(attempts: readonly FlightProviderAttempt[] | null | undefined): boolean {
  return !!attempts?.some((a) => a.outcome === 'unavailable' && !!a.reason && AGE_REASONS.has(a.reason));
}

/** CHD / INF — the passengers whose age the quote needs. */
export function isAgedPaxType(paxType: string | null | undefined): paxType is AgedPaxType {
  const type = paxType?.toUpperCase();
  return type === 'CHD' || type === 'INF';
}

/** A whole age that fits the type; anything else is not sent. */
export function ageFits(paxType: AgedPaxType, age: number): boolean {
  const [min, max] = AGE_RANGE[paxType];
  return Number.isInteger(age) && age >= min && age <= max;
}

/** The form's draft → the ages to send, or the members whose value is missing or out of range. */
export function agesToSend(
  members: ReadonlyArray<{ paxUrn: string; paxType: AgedPaxType }>,
  draft: Readonly<Record<string, string>>,
): { ok: true; ages: PassengerAge[] } | { ok: false; invalid: string[] } {
  const ages: PassengerAge[] = [];
  const invalid: string[] = [];
  for (const m of members) {
    const raw = (draft[m.paxUrn] ?? '').trim();
    const age = /^\d{1,3}$/.test(raw) ? Number(raw) : NaN;
    if (Number.isNaN(age) || !ageFits(m.paxType, age)) invalid.push(m.paxUrn);
    else ages.push({ paxUrn: m.paxUrn, age });
  }
  return invalid.length ? { ok: false, invalid } : { ok: true, ages };
}

/** The `pnr:ages.error.*` key of a refused save; `generic` for anything unexpected. */
export async function agesErrorKey(error: unknown): Promise<string> {
  if (isLockConflictError(error)) return 'locked';
  if (isRebookingConflictError(error)) return KNOWN_ERRORS.has(error.conflict.code) ? error.conflict.code : 'generic';
  if (error instanceof HTTPError) {
    try {
      const body = (await error.response.clone().json()) as { code?: string };
      if (body?.code && KNOWN_ERRORS.has(body.code)) return body.code;
    } catch {
      /* fall through */
    }
  }
  return 'generic';
}

const KNOWN_ERRORS: ReadonlySet<string> = new Set([
  'PASSENGER_AGE_TYPE_MISMATCH',
  'PASSENGER_NOT_IN_GROUP',
  'AGE_NOT_NEEDED',
  'INVALID_PASSENGER_AGES',
  'ALREADY_TICKETED',
  'CASE_NOT_ACTIVE',
  'CASE_REBOOKING_DISABLED',
  'SPECIAL_ASSISTANCE_REQUIRES_VENDOR_CONFIRMATION',
]);
